import { SNHUBLogicPublicDataLocalImpl } from "@api-commons/snhub/impl/SNHUBLogicPublicDataLocalImpl.js";
import { SNHUBLogicPublicData } from "@api-commons/snhub/SNHUBLogicPublicData.js";
import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { BrowserLogger, Logger } from "@ciaopeople/cp-shatsu/Logging";
import { PostMessageProxyPromise } from "@ciaopeople/cp-shatsu/RemoteService";
import { AsyncStorageController } from "@ciaopeople/cp-shatsu/Storage";
import { getParentBaseUrl } from "@ciaopeople/cp-shatsu/Utils";
import { AllAPIClientMethods, APIClientImpl } from "../api/impl/APIClientImpl.js";
import { RESTAPIEventNames } from "../api/RESTAPIEvents.js";
import { ConfigHelper } from "../config/ConfigHelper.js";
import { PropertyConfig } from "../config/PropertyConfig.js";
import { FacebookManualFlowRemotePart } from "../login/direct_providers/fbmanual/FacebookManualFlowRemotePart.js";
import { SessionStorageCookieImpl } from "../session/impl/SessionStorageCookieImpl.js";
import { AllSessionStorageMethods, COOKIE_CHG_EVENT, SessionStorageInterface } from "../session/SessionStorageInterface.js";
import { LocalStorageAsyncController } from "../storage/LocalStorageAsyncController.js";
import { PersonalDataStorageCookieImpl } from "../utils/impl/PersonalDataStorageCookieImpl.js";
import { AllPersonalDataStorageMethods, PersonalDataStorageInterface } from "../utils/PersonalDataStorageInterface.js";
import { ServerSdkInterface } from "./ServerSdkInterface.js";
import sdk_version from "./version.js";



function getNoAppAssociationHost(config: PropertyConfig): string  {
    if(config.noAppAssociationHost && config.noAppAssociationHost.trim() != "") {
        return config.noAppAssociationHost;
    }  
    else {
        var splitten = config.host.split(".");
        splitten[0] = splitten[0] + "-na";
        return splitten.join(".");
    }
}

export class ServerSdk7 implements ServerSdkInterface {


    private logger: Logger;
    private detectedWebview: DetectedWebView;
    private api: APIClientImpl;
    private sessionStorage: SessionStorageInterface;
    private personalDataStorage: PersonalDataStorageInterface;
    private readyPromise: Promise<boolean>;
    private readyPromiseResolver: (b: boolean) => void;
    private snuPublicData: SNHUBLogicPublicData;
    private proxy: PostMessageProxyPromise;
    private fbManualRemote: FacebookManualFlowRemotePart;

    private storageController: AsyncStorageController<string>;




    constructor(public readonly config: PropertyConfig) {


        const noAppAssocHost: string = getNoAppAssociationHost(config);

        this.readyPromise = new Promise((resolve) => {
            this.readyPromiseResolver = resolve;
        });
        this.logger = new BrowserLogger("[ServerSdk-"+ sdk_version+"]", "#f33");
        this.detectedWebview = detectWebView();
        const cookieName = this.config?.membership?.enable?this.config.membership.idtoken_name:null;
        this.api = new APIClientImpl({
            host: config.host,
            secure: config.secure,
            noAppAssociationHostName: noAppAssocHost,
            basePath: "",
            jwtAuth: (cookieName?{
                restReauthUri: "/membership/reauth/rest",
                cookieName: cookieName
            }:null)
        }, this.logger);


        const cfgHelper = new ConfigHelper(config);

        this.sessionStorage = new SessionStorageCookieImpl(
            config.cookies,
            cfgHelper.getSessionStorageConfig(),
            this.logger
        );


        this.snuPublicData = new SNHUBLogicPublicDataLocalImpl(this.config.snhubConfig, 
            this.config.cookies.cookieversion);

        const cooStorage = new PersonalDataStorageCookieImpl(config.cookies.cookieDomain);

        this.personalDataStorage = cooStorage;
        const parentBaseUrl = getParentBaseUrl();
        
        

        this.proxy = new PostMessageProxyPromise(
            {},
            window,
            this.logger,
            window['parent'],
            (origin) => origin.indexOf(parentBaseUrl) >= 0,
            parentBaseUrl
        ); 


        this.fbManualRemote = new FacebookManualFlowRemotePart(this.proxy,
            this.sessionStorage,
            this.config.loginManager.actual_providers["facebook"].applicationIdentifier,
            this.config.loginManager.actual_providers["facebook"].scopes
        );

        this.proxy.registerNamed(this.api, AllAPIClientMethods);
        this.proxy.registerNamed(this.personalDataStorage, AllPersonalDataStorageMethods);
        this.proxy.register("retrieveOrGenerateDeviceId", () => cooStorage.retrieveOrGenerateDeviceId() );
        this.proxy.registerNamed(this.sessionStorage, AllSessionStorageMethods);
        this.proxy.register("isReady", () => this.isReady());
        this.proxy.registerNamed(this.snuPublicData, ["getSNUTokenAnyways"]);
        

        this.proxy.register("isServerReady", () => {
            return this.isReady();
        });

        this.storageController = new LocalStorageAsyncController();
        this.proxy.registerNamed(this.storageController, [
            "getStoredData",
            "setStoredData",
            "deleteStoredData"
        ]);
        


        this.api.onNewsletterSubscriptionStatusChange((data) => {
            this.proxy.invoke(RESTAPIEventNames.NEWSLETTER_SUBSCRIPTION_STATUS_CHANGE, data);
        });
        this.api.onUserNewsletterSubscribeAction((data) => {
            this.proxy.invoke(RESTAPIEventNames.USER_NEWSLETTER_SUBSCRIPTION_ACTION, data);
        });
        this.api.onTopicFollowStatusChange((data) => {
            this.proxy.invoke(RESTAPIEventNames.TOPIC_FOLLOW_STATUS_CHANGE, data);
        });
        this.api.onArticleSaveStatusChange( (data) =>{
            this.proxy.invoke(RESTAPIEventNames.ARTICLE_SAVE_STATUS_CHANGE, data);
        });
        this.api.onDeviceSubscriptionChange((data) => {
            this.proxy.invoke(RESTAPIEventNames.DEVICE_SUBSCRIPTION_CHANGE, data);
        });
        this.api.onUserTopicFollowAction((data) => {
            this.proxy.invoke(RESTAPIEventNames.USER_TOPIC_FOLLOW_ACTION, data);
        });
        this.api.onUserArticleSaveAction( (data) =>{
            this.proxy.invoke(RESTAPIEventNames.USER_ARTICLE_SAVE_ACTION, data);
        });
        this.sessionStorage.onCookieChange((chg) => {
            this.proxy.invoke("SS." + COOKIE_CHG_EVENT, chg);
        });


       

    }
    
    public isReady(): Promise<boolean> {
        return this.readyPromise;
    }

    public init(): void {
        this.logger.log("Called Init()");
        this.readyPromiseResolver(true);
        this.proxy.invoke("serverReadySignal", true).then(ack => {
            this.logger.debug("Client acknowledged readySignal", ack);
        });
    }


    public webviewDetect(): DetectedWebView  {
        return this.detectedWebview;
    }
    
}